import {
  Button,
  CloseButton,
  FileButton,
  Image,
  Loader,
  Progress,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Images, Plus } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { refreshLibraryLists } from "../../api/library-lists";
import { refusalOf, uploadFile } from "../../api/upload-flow";
import { attemptKey, lostAttempt, type LostAttempt } from "../../platform/idempotency";
import {
  IMAGE_TYPES,
  ImagePicker,
  pictureOf,
  type PickedImage,
  type PickerLibrary,
} from "../../platform/media/ImagePicker";
import { toUserMessage } from "../../platform/messages";
import classes from "./ImageField.module.css";

export interface ImageFieldProps {
  /** Names the field's buttons: "image 1" gives "Add image 1", "Remove image 1". */
  name: string;
  /** The chosen picture's alternative text: "Image 1", "Logo". */
  label: string;
  /** A product image's square slot, or the brand logo's 128px tile. */
  shape: "slot" | "logo";
  /** The chosen image's id and picture, or `null` when the field is empty. */
  value: Pick<PickedImage, "assetId" | "url"> | null;
  /** The library an upload goes to and the picker shows; `undefined` when there is none yet. */
  library: PickerLibrary | undefined;
  /** Why there is no library yet, shown under the tile. */
  unavailable?: string;
  onChange: (picked: PickedImage | null) => void;
  /** An upload, the tile's or the picker's, started or ended: the form waits for it before it
   *  saves, and asks before it is left. */
  onBusy?: (busy: boolean) => void;
  error?: string | undefined;
  /** Under the empty tile: a slot's default tag. */
  caption?: ReactNode;
  /** Under the chosen image: a product image's tag. */
  extra?: ReactNode;
}

/**
 * One image of a form as the live forms add it (design-system.md 3.6): clicking the tile opens
 * the file chooser, and the file uploads into the brand's library through the library's one
 * upload path, with its progress on the tile. "From library" chooses an image the library has
 * (A5 decision 27). A chosen image shows with a remove button; the logo's picture replaces it.
 */
export function ImageField(props: ImageFieldProps) {
  const { name, label, shape, value, library, unavailable, onChange, onBusy, error } = props;
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // Through a mutation, so an ended session reaches the shell's one handler (F3).
  const upload = useMutation({ mutationFn: uploadFile });
  const running = useRef<AbortController | null>(null);
  // Clears the chooser after each pick, so the same file can be chosen again.
  const resetInput = useRef<() => void>(null);
  const [progress, setProgress] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [lost, setLost] = useState<LostAttempt | undefined>(undefined);
  // Leaving the form cancels an upload in flight; the flow deletes its pending asset.
  useEffect(() => () => running.current?.abort(), []);

  const send = async (file: File | null) => {
    resetInput.current?.();
    if (file === null || library === undefined) return;
    const refusal = refusalOf(file);
    if (refusal !== undefined || !file.type.startsWith("image/")) {
      setProblem(refusal ?? "Choose an image: JPEG, PNG or WebP.");
      return;
    }
    // One file is one body: the same file again after a lost answer resends its key.
    const body = `${file.name}:${file.size}:${file.lastModified}`;
    const key = attemptKey(body, lost);
    const controller = new AbortController();
    running.current = controller;
    setProblem(null);
    setProgress(0);
    onBusy?.(true);
    try {
      const asset = await upload.mutateAsync({
        ...library,
        file,
        key,
        signal: controller.signal,
        onStep: (step) => setProgress(step.step === "sending" ? step.progress : 0),
      });
      setLost(undefined);
      await refreshLibraryLists(queryClient, library.teamId, library.brandId);
      onChange({ assetId: asset.id, url: pictureOf(asset), name: asset.name });
    } catch (caught) {
      if (controller.signal.aborted) return;
      setProblem(toUserMessage(caught));
      setLost(lostAttempt(key, body, caught));
    } finally {
      running.current = null;
      onBusy?.(false);
    }
  };

  const tile = (open: () => void) => {
    if (upload.isPending) {
      return (
        <div className={classes.tile} data-shape={shape} data-busy>
          <Loader size="sm" aria-hidden />
          <Progress
            className={classes.progress}
            value={progress * 100}
            size="sm"
            aria-label={`Upload of ${name}`}
          />
          <CloseButton
            size="sm"
            className={classes.remove}
            aria-label={`Cancel the upload of ${name}`}
            onClick={() => running.current?.abort()}
          />
        </div>
      );
    }
    if (value === null) {
      return (
        <UnstyledButton
          className={classes.add}
          data-shape={shape}
          aria-label={`Add ${name}`}
          disabled={library === undefined}
          onClick={open}
        >
          {shape === "slot" ? (
            <>
              <span className={classes.addIcon}>
                <Plus size={20} aria-hidden />
              </span>
              <span className={classes.addText}>Add Image</span>
            </>
          ) : (
            <Plus size={20} strokeWidth={1.5} aria-hidden />
          )}
        </UnstyledButton>
      );
    }
    const picture =
      value.url === null || value.url === failed ? (
        // No picture, or its signed URL expired while the form was open: the id still holds.
        <div className={classes.picture} data-shape={shape}>
          <VisuallyHidden>{`${label}: no preview`}</VisuallyHidden>
        </div>
      ) : (
        <Image
          src={value.url}
          alt={label}
          fit="contain"
          className={classes.picture}
          data-shape={shape}
          onError={() => setFailed(value.url)}
        />
      );
    return (
      <div className={classes.tile} data-shape={shape}>
        {shape === "logo" ? (
          // The live logo: clicking the picture chooses another file.
          <UnstyledButton
            className={classes.replace}
            aria-label={`Replace ${name}`}
            disabled={library === undefined}
            onClick={open}
          >
            {picture}
          </UnstyledButton>
        ) : (
          picture
        )}
        <CloseButton
          size="sm"
          className={classes.remove}
          aria-label={`Remove ${name}`}
          onClick={() => onChange(null)}
        />
      </div>
    );
  };

  return (
    <div className={classes.field} data-shape={shape}>
      <FileButton
        resetRef={resetInput}
        accept={IMAGE_TYPES.join(",")}
        disabled={library === undefined || upload.isPending}
        onChange={(file) => void send(file)}
        inputProps={{ "aria-label": `Upload ${name}` }}
      >
        {(button) => tile(button.onClick)}
      </FileButton>
      {value === null ? props.caption : props.extra}
      {library === undefined ? null : (
        <Button
          variant="text"
          size="compact-xs"
          className={classes.fromLibrary}
          leftSection={<Images size={14} aria-hidden />}
          aria-label={`Choose ${name} from the library`}
          disabled={upload.isPending}
          onClick={() => setPicking(true)}
        >
          From library
        </Button>
      )}
      {library === undefined && unavailable !== undefined ? (
        <Text size="xs" c="var(--cai-color-text-muted)" maw={320}>
          {unavailable}
        </Text>
      ) : null}
      {[error, problem].map((message) =>
        message === undefined || message === null ? null : (
          <Text key={message} size="xs" c="var(--cai-color-text-danger)" role="alert">
            {message}
          </Text>
        ),
      )}
      {library === undefined ? null : (
        <ImagePicker
          library={library}
          title={`Choose ${name}`}
          opened={picking}
          onClose={() => setPicking(false)}
          onPick={onChange}
          {...(onBusy === undefined ? {} : { onBusy })}
        />
      )}
    </div>
  );
}
